import { ApiClient } from "../ApiClient";

/**
 * E-Signature API functions
 * Handles all e-signature related API calls
 */

/**
 * Create a new signing session
 * @param {Object} params - Signing session parameters
 * @param {string} params.documentType - Type of document to sign
 * @param {string|number} [params.filingUuid] - Optional filing UUID
 * @param {string|number} [params.userId] - Optional user ID
 * @returns {Promise<Object>} Signing session data with iframe HTML (Parsed JSON)
 */
export const createSigningSession = async (params) => {
  const payload = {
    doc_type: params.documentType,
    ...(params.filingUuid && { filing_uuid: params.filingUuid }),
    ...(params.userId && { user_id: params.userId }),
  };

  const response = await ApiClient.post("/utils/esign", payload);

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return await response.json();
};

/**
 * Prepare 8879 document for CPA
 * @param {Object} params - Prepare 8879 parameters
 * @param {string} params.docType - Type of document (e.g., "8879")
 * @param {number|string} params.filingUuid - Filing ID
 * @param {number|string} [params.userId] - Optional user ID
 * @returns {Promise<Object>} Document data with iframe_url, document_id, status (Parsed JSON)
 */
export const prepare8879 = async (params) => {
  const payload = {
    doc_type: params.docType,
    filing_uuid: params.filingUuid,
    ...(params.userId && { user_id: params.userId }),
  };

  const response = await ApiClient.post("/utils/esign", payload);
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(
      data.message || data.error || `API error: ${response.status}`,
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

/**
 * Get e-signature document details for signing (CUS side)
 * @param {Object} params - Parameters
 * @param {string} params.filingUuid - Filing UUID
 * @param {string} params.docType - Document type (e.g., "efile_signature_authorization")
 * @param {string} params.signerRole - Optional signer role ("taxpayer" or "spouse")
 * @returns {Promise<Object>} Document data with iframe_url, document_id, status (Parsed JSON)
 */
export const getEsignDetails = async (params) => {
  let endpoint = `/utils/esign/${params.filingUuid}/${params.docType}`;

  // Add signer_role as query parameter if provided
  if (params.signerRole) {
    endpoint += `?signer_role=${params.signerRole}`;
  }

  const response = await ApiClient.get(endpoint);
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(
      data.detail || data.message || `API error: ${response.status}`,
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};
